// Owners, at the bottom of their company's Home: the company's name and product name (white label), and in plain words
// how VERA keeps their data private and secure, what recording needs, who is responsible for what, and the laws that
// matter for their work. A summary, not legal advice. Marine (Florida) gets the Florida and US laws by name.

import { useEffect, useState } from "react";
import { crm } from "../lib/crm";
import { COMPANY_CHANGED } from "./StaffAccess";
import { Icon, useStudio } from "./ui";
import { T } from "../lib/i18n";

const PARTS: { title: string; icon: string; points: string[] }[] = [
  { title: "Privacy", icon: "lock", points: [
    "Your records, notes, photos and conversations stay in your company's own space on VERANeural's Microsoft Azure servers in the United States. Other companies on VERA never see them.",
    "VERA reads your email and calendar only after you connect them, with your own sign-in, and sees only what you can see.",
    "Nothing you put in VERA is used to train AI models.",
    "Your location is shared only when you switch Location on, and only with your questions to VERA. She doesn't track where you go.",
    "To get a copy of your data, or to have it deleted, write to support@veraneural.com.",
  ] },
  { title: "Security", icon: "shield", points: [
    "Everyone signs in with their own work account. Each person sees only what their role allows.",
    "Keys for connected apps are kept in a secure vault on VERANeural's servers, never in the app or on your phone.",
    "Everything is encrypted on the way and where it's stored.",
    "Every change VERA or your team makes is in Activity, with who made it, and can be undone.",
  ] },
  { title: "Recording conversations", icon: "mic", points: [
    "Before you record a phone call or a conversation in person, everyone in it must agree. Say it at the start: \"I'm recording this so I don't miss anything. Is that OK?\" If someone says no, take notes instead.",
    "VERA never records a call by herself. Talk with VERA hears only you, while the Talk screen is open.",
    "A voice note you send VERA on WhatsApp becomes text; VERA keeps the words, not the audio. Send only recordings the people in them agreed to.",
  ] },
  { title: "Who is responsible", icon: "consent", points: [
    "VERA helps you work; she doesn't replace your judgement. She can make mistakes. Check anything important before you rely on it: prices, quotes, weather, tides, directions, and anything legal or about safety.",
    "Your company stays responsible for its work, its people, the boats it works on, and what it sends to customers.",
    "VERANeural is not responsible for decisions made from VERA's suggestions, except as set out in your agreement with VERANeural.",
  ] },
];

const LAWS_US_FLORIDA = [
  "Florida's Security of Communications Act (Chapter 934): recording a call or private conversation needs the consent of everyone in it.",
  "Florida Information Protection Act (section 501.171): if customers' personal information may have been exposed, they must be told. Tell VERANeural straight away and we help.",
  "CAN-SPAM Act: marketing emails need your company's real address and an easy way to unsubscribe.",
  "Telephone Consumer Protection Act: marketing texts and automated calls need the customer's written consent first.",
  "Weather and tides in VERA come from NOAA and the National Weather Service. Before going out, check the official forecast and U.S. Coast Guard notices.",
];

export function SafetyCard() {
  const { getToken, toast, profile } = useStudio();
  const [name, setName] = useState(""), [brand, setBrand] = useState(""), [busy, setBusy] = useState(false);
  useEffect(() => { crm.company(getToken).then((c) => { setName(c.name ?? ""); setBrand(c.brand ?? ""); }).catch(() => {}); }, [getToken]);

  async function save() {
    setBusy(true);
    try { await crm.saveCompany(getToken, name.trim(), brand.trim() || undefined); window.dispatchEvent(new Event(COMPANY_CHANGED)); toast(T("Saved.")); }
    catch (e) { toast(e instanceof Error ? e.message : T("Something went wrong.")); } finally { setBusy(false); }
  }

  return (
    <section className="card stack safety">
      <h2>{T("Your company, safety and the law")}</h2>

      <div className="stack" style={{ gap: 6 }}>
        <b>{T("Your name on VERA")}</b>
        <p className="mute">{T("Your people see your company's name, and your own product name if you give one, in Studio and the staff app. A fully licensed copy under your own brand and web address is available from VERANeural.")}</p>
        <div className="row wrap" style={{ gap: 8 }}>
          <input className="input" style={{ flex: 2, minWidth: 200 }} value={name} onChange={(e) => setName(e.target.value)} placeholder={T("Company name")} maxLength={80} />
          <input className="input" style={{ flex: 1, minWidth: 160 }} value={brand} onChange={(e) => setBrand(e.target.value)} placeholder={T("Product name (optional)")} maxLength={60} />
          <button className="btn primary" disabled={busy || !name.trim()} onClick={() => void save()}>{T("Save")}</button>
        </div>
      </div>

      {PARTS.map((p) => (
        <details key={p.title} className="help-fix">
          <summary><Icon name={p.icon} size={14} /> {T(p.title)}</summary>
          <ul>{p.points.map((x) => <li key={x}>{T(x)}</li>)}</ul>
        </details>
      ))}
      <details className="help-fix">
        <summary><Icon name="doc" size={14} /> {T("The laws that matter for your work")}</summary>
        {profile.key === "client-marine"
          ? <ul>{LAWS_US_FLORIDA.map((x) => <li key={x}>{T(x)}</li>)}</ul>
          : <p className="mute">{T("They depend on where your company works. Ask VERANeural at support@veraneural.com for the summary for your country.")}</p>}
      </details>
      <p className="faint small">{T("A plain-language summary, not legal advice. For your own situation, ask your lawyer.")}</p>
    </section>
  );
}
